<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container
        :class="{
          'px-10': !mobile,
          'pt-8': !mobile,
          'px-6': mobile,
          'pt-4': mobile
        }"
      >
        <p class="subtitle-1 font-weight-bold mb-2">Collect Rental Payment</p>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0">
            <p class="caption font-weight-bold">{{ tenantName }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Balance:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(-balance) }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Rent Fee:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(rentFee) }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="mt-4 mb-6"></v-divider>

        <v-form v-model="isFormValid" lazy-validation>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="Start Date"
                    v-bind="attrs"
                    dense
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  scrollable
                  :allowed-dates="allowedStartDates"
                  @input="startDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-select
                v-model="monthsLength"
                :items="monthsLengthOptions"
                hide-details
                label="Payment Length"
                outlined
                dense
              >
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="datePaidMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="datePaid"
                    label="Payment Date"
                    v-bind="attrs"
                    dense
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="datePaid"
                  no-title
                  scrollable
                  @input="datePaidMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-menu
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dueDate"
                    label="Due Date"
                    v-bind="attrs"
                    dense
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="dueDate"
                  no-title
                  scrollable
                  @input="dueDateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-0" cols="12" sm="12" md="4">
              <v-currency-field
                v-model="penalty"
                label="Penalty"
                :disabled="!latePayment"
                :hint="latePaymentHint"
                :rules="penaltyRules"
                class="mb-2"
                persistent-hint
                type="number"
                dense
                outlined
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-alert-octagon</v-icon>
                </template>
              </v-currency-field>
              <v-currency-field
                v-model="total"
                label="Total Amount Paid"
                type="number"
                :rules="amountRules"
                dense
                outlined
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-cash</v-icon>
                </template>
              </v-currency-field>
            </v-col>
            <v-col class="pb-0 d-flex" cols="12" sm="12" md="8">
              <v-card class="flex-grow-1 px-4 py-2" flat outlined>
                <v-row class="ma-0 flex-column">
                  <v-col class="pa-0" cols="12">
                    <v-row class="ma-0">
                      <v-col class="pa-0" cols="5">
                        <p class="caption font-weight-bold">PERIOD</p>
                      </v-col>
                      <v-col class="py-0" cols="7">
                        <p class="caption font-weight-bold">
                          {{ startDate }} to {{ formatDatePicker(endDate) }}
                        </p>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0">
                      <v-col class="pa-0" cols="12">
                        <p class="caption font-weight-bold">CHARGES</p>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0">
                      <v-col class="py-0" cols="5">
                        <p class="caption">- Rent fee:</p>
                      </v-col>
                      <v-col class="py-0" cols="7">
                        <p class="caption">{{ formatCurrency(rentFee) }}</p>
                      </v-col>
                    </v-row>
                    <v-row v-if="latePayment" class="ma-0">
                      <v-col class="py-0" cols="5">
                        <p class="caption">- Penalty:</p>
                      </v-col>
                      <v-col class="py-0" cols="7">
                        <p class="caption">{{ formatCurrency(penalty) }}</p>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0">
                      <v-col class="py-0" cols="5">
                        <p class="caption">Total charges:</p>
                      </v-col>
                      <v-col class="py-0" cols="7">
                        <p class="caption font-weight-bold">
                          {{ formatCurrency(rentFee + penalty) }}
                        </p>
                      </v-col>
                    </v-row>
                    <v-row class="ma-0 mt-3">
                      <v-col class="pa-0" cols="5">
                        <p class="caption font-weight-bold">AMOUNT PAID:</p>
                      </v-col>
                      <v-col class="py-0 d-flex flex-row" cols="7">
                        <p class="caption font-weight-bold">
                          {{ formatCurrency(total) }}
                        </p>
                        <v-tooltip v-if="isUnderPayment" bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="ml-1"
                              color="warning"
                              x-small
                              v-on="on"
                              >mdi-alert-circle-check</v-icon
                            >
                          </template>
                          <p class="white--text">Underpayment</p>
                        </v-tooltip>
                        <v-tooltip v-if="isOverPayment" bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon
                              class="ml-1"
                              color="success"
                              x-small
                              v-on="on"
                              >mdi-arrow-up-bold-circle-outline</v-icon
                            >
                          </template>
                          <p class="white--text">Overpayment</p>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <v-row v-if="useRemainingBalance" class="ma-0">
                      <v-col class="pa-0" cols="5">
                        <p class="caption">Credits used:</p>
                      </v-col>
                      <v-col class="py-0" cols="7">
                        <p class="caption">{{ formatCurrency(balanceUsed) }}</p>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0 pb-2" cols="12" sm="12" md="6">
              <v-checkbox
                v-model="useRemainingBalance"
                :label="useBalanceLabel"
                class="my-0 py-0"
                hide-details
                :disabled="!balance"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-textarea
                v-model="description"
                label="(Optional) Add a description"
                hide-details
                dense
                outlined
                class="my-1"
                rows="3"
              >
              </v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-file-input
                v-model="attachments"
                accept="image/*, application/pdf"
                label="Upload Attachments"
                prepend-icon=""
                chips
                small-chips
                multiple
                dense
                outlined
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-paperclip</v-icon>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-2" cols="12" sm="12" md="12">
              <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
                <template v-slot:label> Send email to tenant </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            text
            :disabled="!isFormValid"
            @click="finish"
            >Finish</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs, DATEPICKER } from '../../util/date'
export default {
  data() {
    return {
      tenantId: -1,
      bedId: -1,
      dialog: false,
      options: {
        color: 'primary',
        width: 700,
        zIndex: 200
      },
      startDate: dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DD'),
      dueDate: dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DD'),
      datePaid: dayjs()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .format('YYYY-MM-DD'),
      startDateMenu: false,
      dueDateMenu: false,
      datePaidMenu: false,
      amount: 0,
      penalty: 0,
      attachments: null,
      loading: true,
      paidUntilDate: dayjs().hour(0).minute(0).second(0).millisecond(0),
      penaltyRules: [
        (v) => Number.isInteger(parseInt(v)) || 'Penalty must be a number.'
      ],
      amountRules: [
        (v) => (v != null && v != undefined) || 'Amount is Required.',
        (v) => Number.isInteger(parseInt(v)) || 'Amount must be a number.'
      ],
      // advance: false,
      tenantName: '',
      balance: 0,
      balanceUsed: 0,
      useRemainingBalance: false,
      useBalanceHint: '',
      total: 0,
      isFormValid: true,
      leaseId: -1,
      bedName: '',
      contractPeriods: [],
      confirmSendEmail: false,
      description: '',
      monthsLength: 1,
      leaseDetails: {}
    }
  },
  computed: {
    latePayment() {
      return this.paidUntilDate.isBefore(dayjs(), 'day')
    },
    latePaymentDays() {
      return dayjs().diff(this.paidUntilDate, 'day')
    },
    latePaymentHint() {
      if (this.latePayment) {
        if (this.latePaymentDays === 1) {
          return `Payment is ${this.latePaymentDays} day late`
        }
        return `Payment is ${this.latePaymentDays} days late`
      } else {
        return 'Payment is on time'
      }
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    useBalanceLabel() {
      if (this.balance >= 0) {
        return `Use Remaining Credits`
      } else {
        return `Include Outstanding Balance`
      }
    },
    isUnderPayment() {
      return this.rentFee + this.penalty > this.total + this.balanceUsed
    },
    isOverPayment() {
      return this.rentFee + this.penalty < this.total + this.balanceUsed
    },
    monthsLengthOptions() {
      const MAX_MONTHS = 24
      const months = [{ value: 1, text: '1 month' }]

      for (let i = 2; i <= MAX_MONTHS; i += 1) {
        months.push({
          value: i,
          text: `${i} months`
        })
      }

      return months
    },
    rentFee() {
      let totalRentFee = 0

      for (let i = 0; i < this.monthsLength; i += 1) {
        const period = this.getPeriod(
          dayjs(this.startDate)
            .add(i + 1, 'month')
            .subtract(1, 'day')
        )

        let rentFee = parseFloat(this.leaseDetails.rentFee)

        if (period) {
          rentFee = period.rentFee
        }

        totalRentFee += rentFee
      }

      return totalRentFee
    },
    endDate() {
      return this.getNormalEndDateFromStartDate(
        dayjs(this.startDate),
        this.monthsLength
      )
    }
  },
  watch: {
    useRemainingBalance: {
      handler() {
        this.useRemainingBalanceEvent()
      }
    },
    rentFee() {
      this.amount = this.rentFee
      if (this.balance > 0) {
        this.balanceUsed =
          this.balance - this.amount >= 0 ? this.amount : this.balance
        this.useRemainingBalance = true
        this.useBalanceHint =
          this.balance > 0
            ? 'Amount reduced by ' + this.formatCurrency(this.balanceUsed)
            : 'Added debit of ' + this.formatCurrency(-this.balanceUsed)
        this.amount =
          this.rentFee - this.balanceUsed >= 0
            ? this.rentFee - this.balanceUsed
            : 0
        this.total = this.amount + parseFloat(this.penalty)
      } else {
        this.balanceUsed = 0
        this.useRemainingBalance = false
        this.useBalanceHint = ''
      }
    }
  },
  methods: {
    async open(tenant) {
      this.leaseDetails = (tenant && tenant.lease_details) || tenant
      // get the current rent fee. If the contract for the next month is a different rent fee,
      // the rental payment fee must be for the next month.
      this.contractPeriods =
        this.leaseDetails.contractPeriods ||
        (this.leaseDetails.contractPeriod &&
          this.leaseDetails.contractPeriod.contractPeriods) ||
        []
      this.paidUntilDate = dayjs(this.leaseDetails.paidUntilDate)

      this.leaseId = tenant.leaseId
      this.loading = true
      this.tenantId = this.leaseDetails.tenantId
      this.bedId = tenant.bedId
      this.bedName = tenant.bedName
      this.startDate = dayjs(
        this.leaseDetails.nextRentalPeriod.startDate
      ).format('YYYY-MM-DD')
      this.dueDate = dayjs(this.leaseDetails.paidUntilDate).format('YYYY-MM-DD')
      this.dialog = true
      this.balance =
        (await this.$store.dispatch('units/fetchTenantBalanceAction', {
          tenantId: this.leaseDetails.tenantId
        })) || 0
      this.confirmSendEmail = false
      this.total = parseFloat(this.leaseDetails.rentFee)

      this.tenantName = tenant.alias || tenant.tenantAlias || ''
      if (!this.tenantName) {
        const tenant_details =
          tenant.tenant_details ||
          (this.leaseDetails && this.leaseDetails.tenant_details)
        if (tenant_details.firstName)
          this.tenantName += tenant_details.firstName
        if (tenant_details.lastName)
          this.tenantName += ' ' + tenant_details.lastName
        if (
          !tenant_details.firstName &&
          !tenant_details.lastName &&
          tenant_details.username
        )
          this.tenantName = tenant_details.username
      }
      this.attachments = []
      //this.options = Object.assign(this.options, options)

      this.loading = false
      this.useRemainingBalanceEvent()
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        let difference =
          this.rentFee +
          parseFloat(this.penalty) -
          (parseFloat(this.total) + parseFloat(this.balanceUsed))
        let confirmMessage = `Are you sure ${this.tenantName} has paid their rent?`
        if (difference > 0) {
          confirmMessage = `The total amount is less than the required payment. This will set the payment as partially paid. Continue?`
        } else if (difference < 0) {
          confirmMessage = `The total amount is more than the required payment. This will CREDIT ${this.formatCurrency(
            Math.abs(difference)
          )} to the tenant's account. Continue?`
        }
        this.loading = true
        if (
          await this.$root.$confirm({
            title: 'Collect Rental Payment',
            message: confirmMessage
          })
        ) {
          await this.$store.dispatch('payments/createRentalPaymentAction', {
            tenantId: this.tenantId,
            bedId: this.bedId,
            amount: this.total,
            rentFee: this.rentFee,
            startDate: dayjs(this.startDate).format(),
            endDate: this.endDate,
            dueDate: dayjs(this.dueDate).format(),
            datePaid: dayjs(this.datePaid).format(),
            attachments: this.attachments,
            description: this.description,
            penalty: parseInt(this.penalty)
              ? {
                  amount: parseFloat(this.penalty),
                  latePaymentDays: this.latePaymentDays
                }
              : null,
            balanceUsed: this.balanceUsed,
            leaseId: this.leaseId,
            confirmSendEmail: this.confirmSendEmail
          })

          this.resolve(true)
          this.dialog = false
          this.loading = false

          this.$root
            .$confirm({
              title: 'Collect Rental Payment',
              message: `The payment has been created.`,
              reconfirmOnly: true,
              confirmText: 'OK',
              options: {
                color: 'primary'
              }
            })
            .then(() => {
              // this.$router.go()
              // window.location.reload()
            })
        } else {
          this.loading = false
        }
      } catch (err) {
        this.loading = false
        this.$root.$confirm({
          title: 'Create Payment Request',
          message: err,
          options: {
            color: 'error'
          },
          reconfirmOnly: true,
          confirmText: 'OK'
        })
      }
    },
    cancel() {
      this.dialog = false
      this.loading = false
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    useRemainingBalanceEvent() {
      if (this.useRemainingBalance) {
        this.balanceUsed =
          this.balance - this.amount >= 0 ? this.amount : this.balance
        this.useBalanceHint =
          this.balance > 0
            ? 'Amount reduced by ' + this.formatCurrency(this.balanceUsed)
            : 'Added debit of ' + this.formatCurrency(-this.balanceUsed)
        this.amount =
          this.rentFee - this.balanceUsed >= 0
            ? this.rentFee - this.balanceUsed
            : 0
        this.total = this.amount
      } else {
        this.balanceUsed = 0
        this.useBalanceHint = ''
        this.amount = this.rentFee
        this.total = this.amount
      }
    },
    allowedStartDates(value) {
      const date = dayjs(value)
      const leaseStartDate = dayjs(this.leaseDetails.nextRentalPeriod.startDate)
      if (leaseStartDate.endOf('month').date() === leaseStartDate.date()) {
        return date.date() === date.endOf('month').date()
      }
      return date.date() === leaseStartDate.date()
    },
    getNormalEndDateFromStartDate(startDate, months) {
      let endDate = startDate.add(months, 'month').subtract(1, 'day')
      const period = this.getPeriod(endDate)
      const moveInDate = period && dayjs(period.moveInDate)

      // check for start at 1st
      if (startDate.date() === 1) {
        return endDate
      }

      // check if end date of next month is greater than move in date - 1
      // e.g. period from 30th to 29th but Feb ends on 28th, so
      // the period will be 30th - 28th. If I just get the
      // next month - 1, it will be 30th - 27th, which is wrong
      if (moveInDate && endDate.date() < moveInDate.date() - 1) {
        // check end of month before setting
        const endOfMonthOfEndDate = endDate.endOf('month')
        if (endOfMonthOfEndDate < moveInDate.date() - 1) {
          endDate = endDate.set('date', endOfMonthOfEndDate)
        } else {
          endDate = endDate.set('date', moveInDate.date() - 1)
        }
      }
      return endDate
    },
    getPeriod(date) {
      const period =
        this.contractPeriods.find((c) => {
          const periodMoveInDate = dayjs(c.moveInDate)
          const periodMoveOutDate = dayjs(c.moveOutDate)
          if (
            !periodMoveInDate.isAfter(date) &&
            !periodMoveOutDate.isBefore(date)
          ) {
            return true
          }
        }) ||
        this.contractPeriods.find((c) => {
          const periodMoveInDate = dayjs(c.moveInDate)

          if (periodMoveInDate.isSameOrAfter(date)) {
            return true
          }
        }) ||
        this.contractPeriods[this.contractPeriods.length - 1]

      return period
    },
    formatDatePicker(date) {
      return dayjs(date).format(DATEPICKER)
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0 !important;
}
</style>
